import React from 'react';
import { ScrollView, StyleSheet } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import { Button } from '@/components/ButtonComponent';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';

type DemoEntry = {
    label: string;
    screen: string;
};

type Group = {
    title: string;
    note: string;
    entries: DemoEntry[];
};

const GROUPS: Group[] = [
    {
        title: 'Card: Push Navigation',
        note: 'Push navigation. Stacks freely on other cards; slides from the right on iOS. Cannot appear above any modal.',
        entries: [{ label: 'card + default', screen: 'NavDemoCard' }],
    },
    {
        title: 'Native Sheets',
        note: 'Slides up as a sheet with the screen behind dimmed. formSheet supports multiple detents (snap points).',
        entries: [
            { label: 'modal / pageSheet (same on iPhone)', screen: 'NavDemoModal' },
            { label: 'formSheet', screen: 'NavDemoFormSheet' },
            { label: 'formSheet with detents (half + full)', screen: 'NavDemoFormSheetDetents' },
        ],
    },
    {
        title: 'Full Screen',
        note: 'Covers the entire screen. No peeking behind, no swipe-to-dismiss gesture.',
        entries: [{ label: 'fullScreenModal', screen: 'NavDemoFullScreen' }],
    },
    {
        title: 'Transparent Overlays',
        note: 'The screen behind stays fully visible and interactive through the overlay. Used for bottom sheets and floating panels in this app (e.g. PlayerProfileSheets, FantasyEventsSheets).',
        entries: [
            { label: 'transparentModal', screen: 'NavDemoTransparentModal' },
            { label: 'containedTransparentModal', screen: 'NavDemoContainedTransparent' },
        ],
    },
    {
        title: 'Contained Modals',
        note: 'Presented within the bounds of the parent view rather than over the whole window.',
        entries: [{ label: 'containedModal', screen: 'NavDemoContainedModal' }],
    },
    {
        title: 'Stack Inside a Modal',
        note: 'A nested stack navigator inside a modal. Pushing new screens stays within the sheet — the sheet itself does not move. Useful for multi-step flows inside a single modal.',
        entries: [
            { label: 'Modal with inner stack', screen: 'NavDemoModalStack' },
            { label: 'Full-screen modal with inner stack', screen: 'NavDemoFullScreenStack' },
        ],
    },
    {
        title: 'Native Headers',
        note: 'Title bar rendered by the OS. Large title collapses on scroll; blur obscures content passing behind. Transparent + systemUltraThinMaterial renders as liquid glass on iOS 26. Header buttons are styled by the OS (liquid glass pill on iOS 26).',
        entries: [
            { label: 'Default header', screen: 'NavDemoHeaderDefault' },
            { label: 'Transparent header (no blur)', screen: 'NavDemoHeaderDefaultTransparent' },
            { label: 'Large title (collapses on scroll)', screen: 'NavDemoHeaderLargeTitle' },
            { label: 'Large title + native header buttons', screen: 'NavDemoHeaderWithButtons' },
            { label: 'Checkout header (close | balance | action)', screen: 'NavDemoHeaderCheckout' },
            { label: 'Scroll edge effects (iOS 26)', screen: 'NavDemoHeaderScrollEdge' },
        ],
    },
    {
        title: 'Animation Variants',
        note: 'Cards support most animations (default is slide from right). Modals on iOS are limited to slide from bottom (default), fade, and none - all other settings fall back to slide from bottom.',
        entries: [
            { label: 'fade', screen: 'NavDemoAnimFade' },
            { label: 'none', screen: 'NavDemoAnimNone' },
            { label: 'simple_push (iOS only)', screen: 'NavDemoAnimSimplePush' },
            { label: 'slide_from_bottom', screen: 'NavDemoAnimSlideFromBottom' },
            { label: 'fade_from_bottom', screen: 'NavDemoAnimFadeFromBottom' },
            { label: 'slide_from_right (Android only - iOS = default)', screen: 'NavDemoAnimSlideFromRight' },
        ],
    },
];

export const NavigationPresentationsDemo = () => {
    const navigation = useNavigation();

    return (
        <Screen>
            <ScreenNavBar title="Navigation Presentations" />
            <ScrollView contentContainerStyle={styles.scroll}>
                <Text variant="bodySmall" color="neutral300" mb="s16">
                    All presentations and animations from @react-navigation/native-stack. Stacking rule: once any
                    non-card screen is open, subsequent card pushes go into the UINavigationController which sits below
                    the entire modal chain.
                </Text>
                {GROUPS.map(group => (
                    <Box key={group.title} mb="s24">
                        <Text variant="labelMedium" color={ds.colorToken.text.primary} mb="s4">
                            {group.title}
                        </Text>
                        <Text variant="bodySmaller" color="neutral300" mb="s12">
                            {group.note}
                        </Text>
                        <Box gap="s8">
                            {group.entries.map(entry => (
                                <Button
                                    key={entry.screen}
                                    label={entry.label}
                                    onPress={() => {
                                        (navigation as never as { navigate: (s: string) => void }).navigate(
                                            entry.screen
                                        );
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                ))}
            </ScrollView>
        </Screen>
    );
};

const styles = StyleSheet.create({
    scroll: {
        padding: 16,
        flexGrow: 1,
    },
});
